import { useState } from 'react';
import { Check, ChevronDown, ChevronRight, X } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';

/**
 * Result row that follows a tool call. Compact one-liner showing the tool
 * name + a short status (✓ ok / ✗ error / first line of output) with a
 * collapsible body containing the full output.
 *
 * The SDK returns these as `user` role messages whose content is a
 * `tool_result` block — without this component, those messages would
 * render as empty user bubbles.
 */
export function ToolResult({
  isError,
  preview,
  full,
  className,
}: {
  isError: boolean;
  preview: string;
  full: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className={cn('min-w-0 max-w-full overflow-hidden rounded-md border bg-muted/30', className)}
    >
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center gap-1.5 px-2.5 py-1.5 text-left text-xs"
        >
          {open ? (
            <ChevronDown className="size-3 shrink-0" />
          ) : (
            <ChevronRight className="size-3 shrink-0" />
          )}
          {isError ? (
            <X className="size-3 shrink-0 text-destructive" />
          ) : (
            <Check className="size-3 shrink-0 text-emerald-500" />
          )}
          <span className="truncate font-mono text-muted-foreground">{preview}</span>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="border-t px-2.5 py-1.5">
        <pre className="overflow-x-auto whitespace-pre-wrap break-all font-mono text-[11px]">
          {full}
        </pre>
      </CollapsibleContent>
    </Collapsible>
  );
}
